<template>
  <div class="music-player">
    <div class="custom-popover-btn" @click="togglePopover">
      <span class="title">𝄞</span>
    </div>
    <!-- 播放器主体 -->
    <div v-if="showPopover" class="player-container mbl" @click.stop>
      <!-- 加载状态 -->
      <div v-if="loading" class="loading">加载音乐列表中...</div>

      <!-- 错误状态 -->
      <div v-if="error" class="error">{{ error }}</div>

      <!-- 正常状态 -->
      <div v-if="!loading && !error && playlist.length > 0">
        <!-- 歌曲信息 -->
        <div class="song-info">
          <div class="song-title">{{ currentSong.title }}</div>
          <div class="song-artist">{{ currentSong.artist }}</div>
        </div>

        <!-- 进度条 -->
        <div class="progress-container">
          <span class="time-current">{{ formatTime(currentTime) }}</span>
          <div class="progress-bar" @click="seekAudio">
            <div class="progress" :style="{ width: progressPercentage + '%' }"></div>
          </div>
          <span class="time-total">{{ formatTime(duration) }}</span>
        </div>

        <!-- 控制按钮 -->
        <div class="controls">
          <button class="control-btn" @click="togglePlayMode" :title="playModeText">
            {{ playModeIcon }}
          </button>
          <button class="control-btn" @click="previousSong">⏮️</button>
          <button class="control-btn play-btn" @click="togglePlay">
            {{ isPlaying ? '⏸️' : '▶️' }}
          </button>
          <button class="control-btn" @click="nextSong">⏭️</button>
          <button class="control-btn" @click="togglePlaylistPanel" title="展开/收起播放列表">
            {{ showPlaylist ? '☰' : '☰' }}
          </button>
        </div>

        <!-- 播放列表 -->
        <div class="playlist-wrapper">
          <div class="playlist-inner" :class="{ 'playlist-expanded': showPlaylist }">
            <div class="playlist-scroll">
              <div
                v-for="(song, index) in playlist"
                :key="index"
                class="song-item"
                :class="{ active: currentIndex === index }"
                @click="playSong(index)"
              >
                <span class="item-index">{{ index + 1 }}</span>
                <div class="item-info">
                  <div class="item-title">{{ song.title }} {{ song.artist }}</div>
                </div>
                <span v-if="currentIndex === index && isPlaying" class="playing-tag">♪</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 无数据状态 -->
      <div v-if="!loading && !error && playlist.length === 0" class="no-music">
        暂无音乐
      </div>
    </div>

    <!-- 隐藏的音频元素 -->
    <audio ref="audioPlayer" @loadedmetadata="updateDuration" @timeupdate="updateProgress" @ended="handleSongEnd"
      @waiting="handleWaiting" @canplay="handleCanPlay" @error="handleAudioError" preload="none">
      您的浏览器不支持音频播放
    </audio>
  </div>
</template>

<script>
import config from '@/utils/Config';
import { getMusicListInfo } from '@/api/music';

const PLAY_MODES = {
  LOOP: 'loop',
  RANDOM: 'random',
  SINGLE: 'single'
};

export default {
  name: 'MusicPlayer',
  data() {
    return {
      showPopover: false,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      volume: 1,
      currentIndex: 0,
      playlist: [],
      loading: false,
      error: '',
      playMode: PLAY_MODES.LOOP,
      isLoadingSong: false,
      showPlaylist: false,
      loadRetryCount: 0,
      maxRetries: 3,
      isBuffering: false,
      isNetworkError: false,
      isAudioLoading: false,
      isFirstLoad: true,
      hasLoadedMetadata: false
    }
  },
  computed: {
    currentSong() {
      return this.playlist[this.currentIndex] || {};
    },
    progressPercentage() {
      return this.duration ? (this.currentTime / this.duration) * 100 : 0;
    },
    playModeText() {
      const modeMap = {
        [PLAY_MODES.LOOP]: '列表循环',
        [PLAY_MODES.RANDOM]: '随机播放',
        [PLAY_MODES.SINGLE]: '单曲循环'
      };
      return modeMap[this.playMode];
    },
    playModeIcon() {
      const iconMap = {
        [PLAY_MODES.LOOP]: '🔁',
        [PLAY_MODES.RANDOM]: '🔀',
        [PLAY_MODES.SINGLE]: '🔂'
      };
      return iconMap[this.playMode];
    }
  },
  mounted() {
    this.fetchMusicList();
    if (this.$refs.audioPlayer) {
      this.$refs.audioPlayer.volume = this.volume;
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    this.cleanupPreloadLinks();
  },
  methods: {
    togglePopover() {
      this.showPopover = !this.showPopover;
      if (this.showPopover) {
        this.$nextTick(() => {
          document.addEventListener('click', this.handleClickOutside);
        });
      } else {
        document.removeEventListener('click', this.handleClickOutside);
        this.showPlaylist = false;
      }
    },

    handleClickOutside(event) {
      const musicPlayer = this.$el;
      if (!musicPlayer.contains(event.target)) {
        this.closePopover();
      }
    },

    closePopover() {
      this.showPopover = false;
      this.showPlaylist = false;
      document.removeEventListener('click', this.handleClickOutside);
    },

    togglePlaylistPanel() {
      this.showPlaylist = !this.showPlaylist;
    },

    async fetchMusicList() {
      this.loading = true;
      this.error = '';

      try {
        const response = await getMusicListInfo();
        
        // 兼容不同的返回数据结构
        let musicData = [];
        if (response.data && Array.isArray(response.data)) {
          musicData = response.data;
        } else if (Array.isArray(response)) {
          musicData = response;
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          musicData = response.data.data;
        } else {
          console.error('未知的数据格式:', response);
          this.error = '数据格式异常';
          this.loading = false;
          return;
        }

        if (musicData.length === 0) {
          this.error = '暂无音乐';
          this.loading = false;
          return;
        }

        this.playlist = musicData.map(item => {
          return {
            title: item.title || item.name || '未知歌曲',
            artist: item.artist || item.author || '未知歌手',
            src: config.BASE_FILE_URL + (item.url || item.src || '')
          };
        });

        if (this.playlist.length > 0) {
          this.currentIndex = 0;
          // 预加载第一首歌但不播放
          this.preloadCurrentSong();
        }
        
        this.loading = false;

      } catch (error) {
        console.error('获取音乐列表错误:', error);
        this.error = '无法连接到音乐服务器，请刷新重试';
        this.loading = false;
      }
    },

    preloadCurrentSong() {
      const audio = this.$refs.audioPlayer;
      const currentSong = this.currentSong;
      if (!audio || !currentSong || !currentSong.src) return;

      if (audio.src !== currentSong.src) {
        audio.src = currentSong.src;
        audio.load();
        this.hasLoadedMetadata = false;
      }
    },

    togglePlayMode() {
      const modes = Object.values(PLAY_MODES);
      const currentIndex = modes.indexOf(this.playMode);
      this.playMode = modes[(currentIndex + 1) % modes.length];
    },

    async loadAndPlay() {
      if (this.isLoadingSong || this.isAudioLoading) return;
      if (this.playlist.length === 0) return;

      this.isLoadingSong = true;
      this.isAudioLoading = true;
      this.error = '';

      try {
        const audio = this.$refs.audioPlayer;
        if (!audio) throw new Error('音频元素不存在');

        const currentSong = this.currentSong;
        if (!currentSong || !currentSong.src) {
          throw new Error('当前歌曲地址无效');
        }

        if (audio.src !== currentSong.src) {
          audio.pause();
          this.isPlaying = false;
          audio.src = currentSong.src;
          audio.load();
          this.currentTime = 0;
          this.duration = 0;
          this.hasLoadedMetadata = false;
        }

        await this.loadAudioWithRetry(audio);
        await this.playAudioWithRetry(audio);

        this.isPlaying = true;
        this.loadRetryCount = 0;
        this.isNetworkError = false;
        this.error = '';

        this.preloadNextSong();

      } catch (error) {
        console.error('加载播放失败:', error);
        this.isPlaying = false;

        if (error.name === 'NotAllowedError') {
          this.error = '请点击页面任意位置后播放';
        } else if (error.message && error.message.includes('超时')) {
          this.error = '网络较慢，加载超时，请点击重试';
          this.isNetworkError = true;
        } else {
          this.error = '歌曲加载失败，请点击重试';
          this.isNetworkError = true;
        }
      } finally {
        this.isLoadingSong = false;
        this.isAudioLoading = false;
      }
    },

    loadAudioWithRetry(audio) {
      return new Promise((resolve, reject) => {
        if (audio.readyState >= 2) {
          resolve();
          return;
        }

        let timeoutId = null;
        let isResolved = false;

        const cleanup = () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          audio.removeEventListener('loadeddata', onSuccess);
          audio.removeEventListener('canplaythrough', onSuccess);
          audio.removeEventListener('error', onError);
        };

        const onSuccess = () => {
          if (isResolved) return;
          isResolved = true;
          cleanup();
          resolve();
        };

        const onError = (e) => {
          if (isResolved) return;
          isResolved = true;
          cleanup();
          reject(e);
        };

        audio.addEventListener('loadeddata', onSuccess);
        audio.addEventListener('canplaythrough', onSuccess);
        audio.addEventListener('error', onError);

        timeoutId = setTimeout(() => {
          if (isResolved) return;
          isResolved = true;
          cleanup();
          const error = new Error('加载超时');
          error.name = 'TimeoutError';
          reject(error);
        }, 15000);
      });
    },

    async playAudioWithRetry(audio) {
      try {
        await audio.play();
        return;
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          throw error;
        }

        if (this.loadRetryCount < this.maxRetries) {
          this.loadRetryCount++;
          console.log(`播放失败，第 ${this.loadRetryCount} 次重试...`);

          await new Promise(resolve => setTimeout(resolve, 1500 * this.loadRetryCount));
          audio.load();
          await this.loadAudioWithRetry(audio);
          return this.playAudioWithRetry(audio);
        } else {
          throw error;
        }
      }
    },

    preloadNextSong() {
      if (this.playlist.length === 0) return;

      let nextIndex;
      if (this.playMode === PLAY_MODES.RANDOM) {
        nextIndex = Math.floor(Math.random() * this.playlist.length);
      } else {
        nextIndex = (this.currentIndex + 1) % this.playlist.length;
      }

      const nextSong = this.playlist[nextIndex];
      if (nextSong && nextSong.src) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'audio';
        link.href = nextSong.src;
        link.dataset.preload = 'true';
        document.head.appendChild(link);

        setTimeout(() => {
          if (link.parentNode) {
            link.parentNode.removeChild(link);
          }
        }, 5000);
      }
    },

    cleanupPreloadLinks() {
      document.querySelectorAll('link[data-preload="true"]').forEach(link => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    },

    togglePlay() {
      if (this.playlist.length === 0) return;

      const audio = this.$refs.audioPlayer;

      if (this.isPlaying) {
        audio.pause();
        this.isPlaying = false;
      } else {
        if (this.isNetworkError || this.isFirstLoad) {
          this.loadAndPlay();
        } else {
          audio.play().then(() => {
            this.isPlaying = true;
          }).catch(error => {
            console.log('播放失败:', error);
            if (error.name === 'NotAllowedError') {
              this.error = '请点击页面任意位置后播放';
            } else {
              this.loadAndPlay();
            }
          });
        }
      }
    },

    previousSong() {
      if (this.playlist.length === 0 || this.isLoadingSong) return;

      if (this.playMode === PLAY_MODES.RANDOM) {
        this.playRandomSong();
      } else {
        this.currentIndex = (this.currentIndex - 1 + this.playlist.length) % this.playlist.length;
        this.loadAndPlay();
      }
    },

    nextSong() {
      if (this.playlist.length === 0 || this.isLoadingSong) return;

      if (this.playMode === PLAY_MODES.RANDOM) {
        this.playRandomSong();
      } else {
        this.currentIndex = (this.currentIndex + 1) % this.playlist.length;
        this.loadAndPlay();
      }
    },

    playRandomSong() {
      if (this.playlist.length === 0) return;

      let newIndex;
      const maxAttempts = 10;
      let attempts = 0;

      do {
        newIndex = Math.floor(Math.random() * this.playlist.length);
        attempts++;
      } while (newIndex === this.currentIndex && this.playlist.length > 1 && attempts < maxAttempts);

      this.currentIndex = newIndex;
      this.loadAndPlay();
    },

    playSong(index) {
      if (this.currentIndex === index && this.isPlaying) return;
      this.currentIndex = index;
      this.loadAndPlay();
    },

    seekAudio(event) {
      if (this.playlist.length === 0 || !this.duration) return;

      const progressBar = event.currentTarget;
      const clickX = event.offsetX;
      const width = progressBar.clientWidth;
      const seekTime = (clickX / width) * this.duration;

      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.currentTime = seekTime;
        this.currentTime = seekTime;
      }
    },

    updateDuration() {
      this.duration = this.$refs.audioPlayer.duration || 0;
      this.hasLoadedMetadata = true;
    },

    updateProgress() {
      if (this.$refs.audioPlayer) {
        this.currentTime = this.$refs.audioPlayer.currentTime || 0;
      }
    },

    handleSongEnd() {
      if (this.playMode === PLAY_MODES.SINGLE) {
        const audio = this.$refs.audioPlayer;
        if (audio) {
          audio.currentTime = 0;
          audio.play().catch(() => {
            this.loadAndPlay();
          });
        }
      } else {
        this.nextSong();
      }
    },

    handleWaiting() {
      this.isBuffering = true;
    },

    handleCanPlay() {
      this.isBuffering = false;
    },

    handleAudioError(event) {
      console.error('音频错误:', event);
      const audio = this.$refs.audioPlayer;
      if (audio && audio.error) {
        const errorCode = audio.error.code;
        if (errorCode === 4) {
          this.error = '音频加载失败，请检查网络后重试';
          this.isNetworkError = true;
        }
      }
    },

    reloadMusic() {
      this.loadRetryCount = 0;
      this.isNetworkError = false;
      this.error = '';
      this.fetchMusicList();
    },

    formatTime(seconds) {
      if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const sec = Math.floor(seconds % 60);
      return `${mins}:${sec.toString().padStart(2, '0')}`;
    }
  },
  watch: {
    volume(newVolume) {
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.volume = newVolume;
      }
    }
  }
}
</script>

<style scoped>
.music-player {
  position: fixed;
  right: 6px;
  bottom: 155px;
  z-index: 9999;
}

@media only screen and (max-width: 768px) {
  .title {
    font-size: 20px !important;
  }
}

.custom-popover-btn {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.4);
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-weight: 600;
  backdrop-filter: blur(10px);
}

.custom-popover-btn:hover {
  transform: scale(1.15);
  box-shadow: 0 0 12px rgba(255, 180, 180, 0.3);
}

.player-container {
  position: absolute;
  right: 60px;
  bottom: 0;
  border-radius: 10px;
  padding: 26px 22px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.loading,
.error,
.no-music {
  text-align: center;
  padding: 40px 20px;
  color: var(--font-color-1);
  font-size: 16px;
}

.error {
  color: var(--font-color-1);
}

.song-info {
  text-align: center;
  margin-bottom: 10px;
}

.song-title {
  font-size: 16px;
  font-weight: bold;
  color: var(--font-color-1);
  margin-bottom: 5px;
}

.title {
  font-size: 30px;
  color: #fff;
}

.song-artist {
  font-size: 12px;
  color: var(--font-color-4);
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.time-current,
.time-total {
  font-size: 12px;
  color: var(--font-color-4);
  min-width: 35px;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  cursor: pointer;
  position: relative;
}

.progress {
  height: 100%;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 2px;
  transition: width 0.1s ease;
}

.controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.control-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.control-btn:hover {
  background: rgba(79, 172, 254, 0.1);
  transform: scale(1.1);
}

.play-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  font-size: 24px;
  padding: 12px;
}

.volume-container {
  margin-bottom: 15px;
}

.volume-bar {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  cursor: pointer;
  position: relative;
}

.volume-level {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 2px;
  transition: width 0.2s ease;
}

.playlist-wrapper {
  overflow: hidden;
  margin-top: 0;
}

.playlist-inner {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    margin-top 0.3s ease;
  margin-top: 0;
}

.playlist-inner.playlist-expanded {
  max-height: 220px;
  opacity: 1;
  margin-top: 16px;
  transition: max-height 0.6s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    margin-top 0.4s ease;
}

.playlist-scroll {
  max-height: 180px;
  overflow-y: auto;
  padding-right: 4px;
}

.playlist-scroll::-webkit-scrollbar {
  width: 4px;
}

.playlist-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.playlist-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  transition: background 0.3s ease;
}

.playlist-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.song-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.25s ease, transform 0.2s ease;
  margin-bottom: 4px;
}

.song-item:hover {
  background: rgba(79, 172, 254, 0.12);
  transform: translateX(4px);
}

.song-item.active {
  background: rgba(79, 172, 254, 0.25);
}

.song-item:active {
  transform: scale(0.97);
}

.item-index {
  width: 6px;
  text-align: center;
  font-size: 12px;
  flex-shrink: 0;
}

.item-info {
  flex: 1;
  overflow: hidden;
  min-width: 0;
}

.item-title {
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.playing-tag {
  color: #4facfe;
  font-size: 14px;
  animation: pulse 1.2s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.15);
  }
}

.song-item-enter-active {
  animation: slideIn 0.35s ease forwards;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>